<template>
  <tr class="row-data">
    <td
      @click="$emit('clickOnDateCell', date)"
      class="day-data"
      :class="[color_class, { weekend: isWeekend }]"
    >
      <div class="cell" v-if="!isSummary">
        <div style="gap: 3px" class="d-flex flex-column w-100">
          <div class="d-flex justify-start pl-2 pr-1">
            <span v-html="day_of_week"></span>
            <v-tooltip v-if="is_today" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                  color="secondary"
                  size="14"
                  class="ml-1"
                >
                  mdi-calendar-today
                </v-icon>
              </template>
              <span class="text-uppercase">{{ $t("today") }}</span>
            </v-tooltip>
            <v-spacer></v-spacer>
            <!-- Los avisos de pricing van aquí, con los comentarios y los
                 eventos: son cosas que pasan ese día, no parte del precio -->
            <a-rms-strategy-day-alerts
              v-if="alerts.length > 0"
              :date="date"
              :alerts="alerts"
            />
            <a-rms-strategy-day-comments
              v-if="comments.length > 0"
              :date="date"
              :comments="comments"
            />
            <a-rms-strategy-day-events
              v-for="impact in impacts"
              :key="impact.impact"
              :impact="impact"
              :events="events"
              :date="date"
            />
          </div>
          <div
            v-if="density === 'default' && kpiMode !== 'hide_vs'"
            class="d-flex justify-start pl-2 grey--text"
          >
            <span v-html="day_of_week_vs"></span>
            <v-spacer></v-spacer>
            <a-rms-strategy-day-events
              v-for="impact_vs in impacts_vs"
              :key="impact_vs.impact"
              :impact="impact_vs"
              :events="events_vs"
              :date="date_vs"
            />
          </div>
        </div>
      </div>
      <div class="cell justify-start" v-else>
        <div class="d-flex justify-start pl-2">
          <span>{{ $t("result_period") }}</span>
        </div>
      </div>
    </td>
    <td
      v-for="column in nonStickyColumnHeaders"
      :key="`${date}-${column.group}-${column.column}`"
      :class="[
        getClass(
          configStrategyTable[column.group].columns[column.column].kpi,
          getValue(column),
          column
        ),
        {
          'sticky-columns-shadow': column.column === lastStickyColumn.column
        },
        { weekend: isWeekend },
        { previous: isPrevious },
        { summary: isSummary },
        { 'group-otb': column.group === 'OTB' },
        { isSelectedChangePrice: isSelectedChangePrice(column) },
        { 'rate-change-bg': column.column === 'rate_change' },
        {
          'td-suggestion-ignored':
            column.column === 'rate_change' &&
            daily_suggestions[date] &&
            daily_suggestions[date].status === 'ignored'
        }
      ]"
      @mouseover="$emit('hoveredKpi', column)"
      @mouseleave="$emit('hoveredKpi', '')"
    >
      <div
        class="cell px-1 position-relative h-100 w-100"
        :class="{
          'min-height': density === 'default' && kpiMode === 'hide_vs'
        }"
      >
        <template v-if="column.group.startsWith('pickup_')">
          <v-row class="mx-0 top-line">
            <v-col
              :cols="density === 'default' ? 7 : 5"
              class="pa-0 d-flex justify-end"
            >
              <a-rms-day-pickup-tooltip
                v-if="getStaticKpiValue(column) && date"
                :date="date"
                :spot="Number(column.group.slice(-1))"
                class="mr-1"
              ></a-rms-day-pickup-tooltip>
            </v-col>
            <v-col
              :cols="density === 'default' ? 5 : 7"
              class="pa-0 d-flex justify-end"
            >
              <a-kpi
                :kpi="
                  configStrategyTable[column.group].columns[column.column].kpi
                "
                :value="getStaticKpiValue(column)"
                :absolute="kpiMode === 'absolute'"
                x_small
              />
            </v-col>
          </v-row>
          <v-row
            v-if="
              rms.strategy_vs_spot === 'stly' &&
                density === 'default' &&
                kpiMode !== 'hide_vs'
            "
            class="mx-0 mt-1 align-center bottom-line"
          >
            <v-col cols="12" class="pa-0 d-flex justify-end">
              <a-kpi
                v-if="rms.strategy_vs_spot === 'stly'"
                :kpi="
                  configStrategyTable[column.group].columns[column.column].kpi
                "
                :value="getStaticKpiValue(column)"
                :vs_values="[
                  getStaticKpiValue(
                    column,
                    `pickup_stly_${column.group.slice(-1)}`
                  )
                ]"
                :vs_periods="[rms.strategy_vs_spot]"
                :absolute="kpiMode === 'absolute'"
                xxx_small
                hide_kpi
                hide_tooltip
                shorten
                iconRight
              />
            </v-col>
            <v-col cols="12" class="pa-0 d-flex justify-end">
              <a-kpi
                :kpi="
                  configStrategyTable[column.group].columns[column.column].kpi
                "
                :value="
                  getStaticKpiValue(
                    column,
                    `pickup_stly_${column.group.slice(-1)}`
                  )
                "
                :absolute="kpiMode === 'absolute'"
                class="kpi-vs"
                xx_small
              />
            </v-col>
          </v-row>
        </template>
        <template
          v-else-if="
            column.group === 'rates' &&
              column.column === 'rate_change' &&
              !isSummary &&
              isTodayOrFuture(date)
          "
        >
          <!-- Una entrada de només avisos no proposa preu: la cel·la es
               comporta com si no hi hagués recomanació. L'avís es veu a la
               columna de la data, amb els comentaris i els esdeveniments. -->
          <template
            v-if="
              !daily_suggestions[date] ||
                daily_suggestions[date].notify_only ||
                currentState(daily_suggestions[date]) === 'accepted'
            "
          >
            <a-rms-apply-prices
              :price="getValue({ group: 'rates', column: 'current_rate' })"
              :date="date"
              @checkbox-changed="checkboxPriceChanged"
            />
          </template>
          <template
            v-if="
              daily_suggestions[date] && !daily_suggestions[date].notify_only
            "
          >
            <v-overlay
              v-if="daily_suggestions[date].status === 'ignored'"
              color="secondary"
              class="suggestion-ignored-overlay h-100 w-100"
              absolute
            >
              <v-btn
                rounded
                x-small
                color="green"
                @click="reactivateSuggestion(daily_suggestions[date].id)"
              >
                <v-icon size="14" class="mr-1">
                  mdi-reload
                </v-icon>
                {{ $t("Reactivate") }}
              </v-btn>
            </v-overlay>
            <a-pricing-strategy-apply-strategy
              v-else
              :suggestion="daily_suggestions[date]"
              :date="date"
              :density="density"
              showCheckbox
              hideAcceptedSuggestion
              @suggestion-selection-changed="checkboxSuggestionChanged"
            />
          </template>
        </template>
        <template v-else>
          <v-row class="mx-0 top-line">
            <template
              v-if="
                column.group === 'rates' &&
                  column.column === 'current_rate' &&
                  has_pvp_change_today
              "
            >
              <v-col cols="2" class="pa-0 d-flex justify-end">
                <a-rms-pvp-evolution-tooltip
                  :date="date"
                  class="mr-1"
                ></a-rms-pvp-evolution-tooltip>
              </v-col>
              <v-col
                cols="5"
                class="pa-0 d-flex justify-end"
                v-if="density === 'default'"
              >
                <a-kpi
                  :kpi="
                    configStrategyTable[column.group].columns[column.column].kpi
                  "
                  :value="getValue(column)"
                  :vs_values="[
                    getValue(column) - pvpMlosChanges('pvp', 'pickup0')
                  ]"
                  :vs_periods="['previous_price']"
                  absolute
                  class="mr-1"
                  xxx_small
                  hide_kpi
                  hide_icon
                  iconRight
                  show_plus_sign
                />
              </v-col>
              <v-col
                :cols="density === 'default' ? 5 : 7"
                class="pa-0 d-flex justify-end"
              >
                <a-kpi
                  :kpi="
                    configStrategyTable[column.group].columns[column.column].kpi
                  "
                  :value="getValue(column)"
                  :absolute="kpiMode === 'absolute'"
                  x_small
                />
              </v-col>
            </template>
            <template
              v-else-if="
                column.group === 'rates' &&
                  column.column === 'current_rate' &&
                  !has_pvp_change_today
              "
            >
              <v-col cols="2" class="pa-0 d-flex justify-end">
                <a-rms-pvp-evolution-tooltip
                  :date="date"
                  class="mr-1"
                ></a-rms-pvp-evolution-tooltip>
              </v-col>
              <v-col
                cols="5"
                class="pa-0 d-flex justify-end"
                v-if="density === 'default'"
              >
              </v-col>
              <v-col
                :cols="density === 'default' ? 5 : 7"
                class="pa-0 d-flex justify-end"
              >
                <a-kpi
                  :kpi="
                    configStrategyTable[column.group].columns[column.column].kpi
                  "
                  :value="getValue(column)"
                  :absolute="kpiMode === 'absolute'"
                  x_small
                />
              </v-col>
            </template>
            <template
              v-else-if="
                column.group === 'rates' &&
                  column.column === 'compset_avg_min_price'
              "
            >
              <v-col cols="7" class="pa-0 d-flex justify-end">
                <a-rms-compset-tooltip
                  v-if="all_kpis_daily_compset"
                  :date="date"
                  :compset-avg="getValue(column)"
                  class="mr-1"
                  pickup
                ></a-rms-compset-tooltip>
              </v-col>
              <v-col
                cols="5"
                :class="getColorCompetitorsClass(column)"
                class="pa-0 d-flex justify-end"
              >
                <a-kpi
                  :kpi="
                    configStrategyTable[column.group].columns[column.column].kpi
                  "
                  :value="getValue(column)"
                  :absolute="kpiMode === 'absolute'"
                  x_small
                />
              </v-col>
            </template>
            <template v-else>
              <v-col
                :class="getColorCompetitorsClass(column)"
                class="pa-0 d-flex justify-end"
              >
                <a-kpi
                  :kpi="
                    configStrategyTable[column.group].columns[column.column].kpi
                  "
                  :value="getValue(column)"
                  :absolute="kpiMode === 'absolute'"
                  x_small
                />
              </v-col>
            </template>
          </v-row>
          <v-row
            v-if="density === 'default' && kpiMode !== 'hide_vs'"
            class="mx-0 mt-1 align-center bottom-line"
          >
            <v-col cols="7" class="pa-0 d-flex justify-end">
              <a-kpi
                :kpi="
                  configStrategyTable[column.group].columns[column.column].kpi
                "
                :value="getValue(column)"
                :vs_values="[getValue(column, rms.strategy_vs_spot)]"
                :vs_periods="[rms.strategy_vs_spot]"
                :absolute="kpiMode === 'absolute'"
                xxx_small
                hide_kpi
                hide_tooltip
                iconRight
                shorten
              />
            </v-col>
            <v-col cols="5" class="pa-0 d-flex justify-end">
              <a-kpi
                :kpi="
                  configStrategyTable[column.group].columns[column.column].kpi
                "
                :value="getValue(column, rms.strategy_vs_spot)"
                :absolute="kpiMode === 'absolute'"
                class="kpi-vs"
                xx_small
              />
            </v-col>
          </v-row>
        </template>
      </div>
      <div
        v-if="
          showProgressPercent(
            configStrategyTable[column.group].columns[column.column].kpi
          )
        "
        class="percent-progress"
        :style="getPercentStyle(getValue(column))"
      ></div>
    </td>
  </tr>
</template>

<script>
import * as Utils from "@/utils.js";
import { getFriendlyDayStrategy } from "@/utils.js";
import Vue from "vue";
import { mapGetters, mapState } from "vuex";

import aPricingStrategyApplyStrategy from "@/components/pricing-strategy/aPricingStrategyApplyStrategy.vue";
import aRmsApplyPrices from "@/components/rms/aRmsApplyPrices.vue";
import aRmsStrategyDayAlerts from "@/components/rms/aRmsStrategyDayAlerts";
import aRmsStrategyDayComments from "@/components/rms/aRmsStrategyDayComments";
import aRmsStrategyDayEvents from "@/components/rms/aRmsStrategyDayEvents";
import aRmsCompsetTooltip from "@/components/rms/axel/aRmsCompsetTooltip.vue";
import aRmsDayPickupTooltip from "@/components/rms/axel/aRmsDayPickupTooltip.vue";
import aRmsPvpEvolutionTooltip from "@/components/rms/axel/aRmsPvpEvolutionTooltip.vue";
import aKpi from "@/components/rms_ui/aKpi.vue";

export default {
  name: "a-rms-strategy-day-table-row",
  emits: [
    "clickOnDateCell",
    "hoveredKpi",
    "price-selection-changed",
    "suggestion-selection-changed"
  ],
  components: {
    aKpi,
    aRmsStrategyDayAlerts,
    aRmsStrategyDayComments,
    aRmsStrategyDayEvents,
    aPricingStrategyApplyStrategy,
    aRmsApplyPrices,
    aRmsCompsetTooltip,
    aRmsDayPickupTooltip,
    aRmsPvpEvolutionTooltip
  },
  props: {
    row: Object,
    date: String,
    nonStickyColumnHeaders: Array,
    lastStickyColumn: Object,
    configStrategyTable: Object,
    lastColumnOfEachGroup: Object,
    isSummary: Boolean,
    kpiMode: String,
    density: String
  },
  data: () => ({
    kpis_data: {},
    selected_prices: {},
    selected_suggestions: {}
  }),
  methods: {
    checkboxPriceChanged(event) {
      this.$set(this.selected_prices, event.date, event);
      this.$emit("price-selection-changed", event);
    },
    checkboxSuggestionChanged(event) {
      this.$set(this.selected_suggestions, event.date, event);
      this.$emit("suggestion-selection-changed", event);
    },
    isTodayOrFuture(date) {
      return Vue.moment(date).isSameOrAfter(Vue.moment().format("YYYY-MM-DD"));
    },
    // Casos basados en comparación de precios
    isSuggestionAccepted(suggestion) {
      return suggestion.price_current === suggestion.price_suggestion;
    },
    isPriceUnchanged(suggestion) {
      return suggestion.price_initial === suggestion.price_current;
    },
    isUserModifiedPrice(suggestion) {
      return (
        suggestion.price_initial !== suggestion.price_current &&
        suggestion.price_current !== suggestion.price_suggestion
      );
    },
    // Estado actual basado en los casos
    currentState(suggestion) {
      if (this.isSuggestionAccepted(suggestion)) {
        return "accepted";
      } else if (this.isPriceUnchanged(suggestion)) {
        return "pending";
      } else if (this.isUserModifiedPrice(suggestion)) {
        return "modified";
      }
      return "pending";
    },
    showProgressPercent(kpi) {
      let type = this.kpis_data[kpi]
        ? this.kpis_data[kpi].data_format
        : "default";
      return type === "percent1" || type === "percent2";
    },
    getPercentStyle(value) {
      return "width:" + value + "%;";
    },
    getKpiPercentClassByValue(value) {
      if (value < 20) {
        return "kpi-percent-range-0-20";
      } else if (value >= 20 && value < 40) {
        return "kpi-percent-range-20-40";
      } else if (value >= 40 && value < 60) {
        return "kpi-percent-range-40-60";
      } else if (value >= 60 && value < 80) {
        return "kpi-percent-range-60-80";
      } else if (value >= 80) {
        return "kpi-percent-range-80-100";
      }
    },
    getClass(kpi, value, column) {
      // If type of kpi is percent we must add a class to the cell named  kpi-percent-range-0-20, kpi-percent-range-20-40, kpi-percent-range-40-60, kpi-percent-range-60-80, kpi-percent-range-80-100
      let type = this.kpis_data[kpi]
        ? this.kpis_data[kpi].data_format
        : "default";
      if (value === null) return "";
      if (column.column === "occupancy") {
        return this.getKpiPercentClassByValue(100 - value);
      } else if (
        type === "percent0" ||
        type === "percent1" ||
        type === "percent2"
      ) {
        return this.getKpiPercentClassByValue(value);
      } else if (column.group === "roomtype") {
        return !this.isSummary &&
          value > this.configStrategyTable.roomtype.columns[column.column].limit
          ? "limit"
          : "";
      } else if (column.group === "pickup_4") {
        if (
          this.configStrategyTable[column.group].columns[column.column].min &&
          this.configStrategyTable[column.group].columns[column.column].max
        ) {
          // rescale the value with min and max
          const { min, max } = this.configStrategyTable[column.group].columns[
            column.column
          ];
          let scaledValue = ((value - min) / (max - min)) * 100;
          return this.getKpiPercentClassByValue(100 - scaledValue);
        }
      } else if (column.group.startsWith("pickup_")) {
        return value <=
          this.configStrategyTable[column.group].columns[column.column].limit
          ? "limit"
          : "";
      } else if (
        column.group === "inventory" &&
        (column.column === "available" || column.column === "available_pace")
      ) {
        return !this.isSummary &&
          value <=
            this.configStrategyTable[column.group].columns[column.column].limit
          ? "limit"
          : "";
      }

      return "";
    },
    getCityMKDKpiValue(column, vs) {
      const value =
        column.column === "MKD-Final" || vs
          ? this.getStaticKpiValue(column, vs)
          : this.getStaticKpiValue(
              { column: "MKD-Final", group: "cityMKD" },
              vs
            ) - this.getStaticKpiValue(column, vs);
      // Customer wants to see availability, not demand
      // If showing the vs value, 0 means no value, but in current value, 0 means 100% availability
      return vs
        ? value > 0
          ? 100 - value
          : value
        : value >= 0
        ? 100 - value
        : value;
    },
    getUniqueKpis(configTable) {
      const kpisSet = new Set();

      // Iterate through each category in the config table
      Object.values(configTable).forEach(category => {
        // Check if the category has columns
        if (category.columns) {
          // Iterate through each column in the category
          Object.values(category.columns).forEach(column => {
            // If the column has a kpi property, add it to the set
            if (column.kpi) {
              kpisSet.add(column.kpi);
            }
          });
        }
      });

      // Convert the set to an array and return
      return Array.from(kpisSet);
    },
    getColorCompetitorsClass(column) {
      const value = this.getValue(column);
      if (
        column.group === "rates" &&
        (column.column === "compset_avg_min_price" ||
          column.column === "compset_min_price")
      ) {
        const current_price = this.getValue({
          group: "rates",
          column: "current_rate"
        });
        return value > current_price ? "red--text" : "green--text";
      }
      return "";
    },
    pvpMlosChanges(kpi, spot) {
      return this.rms.pvp_mlos_changes_daily &&
        this.rms.pvp_mlos_changes_daily[this.date] &&
        this.rms.pvp_mlos_changes_daily[this.date][kpi]
        ? this.rms.pvp_mlos_changes_daily[this.date][kpi][spot]
        : null;
    },
    reactivateSuggestion(suggestion_id) {
      this.$store.dispatch("setSnackbar", {
        title: this.$t("reactivating_price_suggestion"),
        color: "secondary"
      });
      const data = {
        hotel_id: this.current_hotel.id,
        suggestions: [{ id: suggestion_id }]
      };
      this.$store
        .dispatch("reactivatePricingStrategySuggestion", data)
        .then(() => {
          this.$store.dispatch("setSnackbar", {
            title: this.$t("price_suggestion_reactivated"),
            color: "accent"
          });
        })
        .catch(error => {
          Utils.showError(this, "price_suggestion_not_reactivated");
        });
    },
    isSelectedChangePrice(column) {
      if (column.group === "rates" && column.column === "rate_change") {
        return (
          (this.selected_prices &&
            this.selected_prices[this.date] &&
            this.selected_prices[this.date].selected) ||
          (this.selected_suggestions &&
            this.selected_suggestions[this.date] &&
            this.selected_suggestions[this.date].selected)
        );
      }
      return false;
    },
    initKpisData() {
      this.kpis = this.getUniqueKpis(this.configStrategyTable);
      const kpis_data = {};
      this.kpis.forEach(kpi => {
        kpis_data[kpi] = this.get_kpi(kpi);
      });
      this.kpis_data = Object.freeze(kpis_data);
    }
  },
  computed: {
    isWeekend() {
      return !this.isSummary && Vue.moment(this.date).isoWeekday() > 4;
    },
    isPrevious() {
      return (
        !this.isSummary &&
        Vue.moment(this.date).isBefore(
          Vue.moment()
            .endOf("day")
            .add(-1, "days")
        )
      );
    },
    // Data key of the segment dimension in the KPIs response
    segment_dimension_key() {
      return this.current_hotel &&
        this.current_hotel.settings &&
        this.current_hotel.settings.marketsegments_in_day_kpis
        ? "marketsegment"
        : "segment";
    },
    getStaticKpiValue() {
      return function(column, vs = undefined) {
        const spotIndex = vs
          ? vs
          : this.configStrategyTable[column.group] &&
            this.configStrategyTable[column.group].columns[column.column].block;
        return this.row &&
          this.row[spotIndex] &&
          this.configStrategyTable[column.group] &&
          (this.configStrategyTable[column.group].columns[column.column].zero ||
            this.row[spotIndex][
              this.configStrategyTable[column.group].columns[column.column].kpi
            ])
          ? this.row[spotIndex][
              this.configStrategyTable[column.group].columns[column.column].kpi
            ]
          : null;
      };
    },
    getDynamicKpiValue() {
      return function(column, dynamicKey = "roomtype", vs = undefined) {
        const spotIndex = vs
          ? vs
          : this.configStrategyTable[column.group].columns[column.column].block;

        return this.row &&
          this.row[spotIndex] &&
          this.row[spotIndex][dynamicKey] &&
          this.row[spotIndex][dynamicKey][column.column] &&
          this.row[spotIndex][dynamicKey][column.column][
            this.configStrategyTable[column.group].columns[column.column].kpi
          ]
          ? this.row[spotIndex][dynamicKey][column.column][
              this.configStrategyTable[column.group].columns[column.column].kpi
            ]
          : null;
      };
    },
    getValue() {
      return function(column, vs = undefined) {
        if (column.group === "roomtype") {
          return this.getDynamicKpiValue(column, "roomtype", vs);
        } else if (column.group === "segment") {
          return this.getDynamicKpiValue(
            column,
            this.segment_dimension_key,
            vs
          );
        } else if (column.group === "cityMKD") {
          return this.getCityMKDKpiValue(column, vs);
        } else if (column.column === "room_revenue_other") {
          return (
            this.getStaticKpiValue(
              { column: "room_revenue_total", group: "revenue" },
              vs
            ) -
            this.getStaticKpiValue(
              { column: "room_revenue", group: "revenue" },
              vs
            )
          );
        } else {
          return this.getStaticKpiValue(column, vs);
        }
      };
    },
    color_class() {
      if (!this.row || !this.row.otb) return "neutral";

      let diff = this.row.otb.available_pace;
      if (diff > this.paceLimit) return "positive";
      if (diff < this.paceLimit) return "negative";
      return "neutral";
    },
    paceLimit() {
      return Math.round(
        this.roomtype_types_mapped.reduce((accRoomType, curRoomType) => {
          return accRoomType
            ? accRoomType + curRoomType.available_rooms
            : curRoomType.available_rooms;
        }, 0) * 0.2
      );
    },
    day_of_week() {
      return getFriendlyDayStrategy(this.date, true);
    },
    day_of_week_vs() {
      return getFriendlyDayStrategy(this.date_vs, true);
    },
    is_today() {
      return this.date === Vue.moment().format("YYYY-MM-DD");
    },
    events() {
      if (
        this.calendar_store.hotel_comparative_dates &&
        this.calendar_store.hotel_comparative_dates[this.date] &&
        this.calendar_store.hotel_comparative_dates[this.date].events
      ) {
        return this.calendar_store.hotel_comparative_dates[this.date].events;
      }
      return [];
    },
    events_vs() {
      if (
        this.date_vs &&
        this.calendar_store.hotel_comparative_dates &&
        this.calendar_store.hotel_comparative_dates[this.date_vs] &&
        this.calendar_store.hotel_comparative_dates[this.date_vs].events
      ) {
        return this.calendar_store.hotel_comparative_dates[this.date_vs].events;
      }
      return [];
    },
    impacts() {
      let events_count = this.calendar_store.impacts.map(i => {
        return {
          impact: i.value,
          color: i.color,
          count: this.events.filter(e => e.impact === i.value).length
        };
      });
      return events_count.filter(e => e.count > 0);
    },
    impacts_vs() {
      let events_count = this.calendar_store.impacts.map(i => {
        return {
          impact: i.value,
          count: this.events_vs.filter(e => e.impact === i.value).length
        };
      });
      return events_count.filter(e => e.count > 0);
    },
    date_vs() {
      let date_vs = "";

      // Para el budget
      if (this.rms.strategy_vs_spot === "bud") {
        return Vue.moment(this.date);
      }

      // Si es LY o STLY, usar la misma fecha del año anterior (cálculo directo)
      if (
        this.rms.strategy_vs_spot === "lyn" ||
        this.rms.strategy_vs_spot === "stlyn"
      ) {
        return Vue.moment(this.date)
          .subtract(1, "year")
          .format("YYYY-MM-DD");
      }

      // Si es STLYN o LYN (o por defecto), usar date_vs del store
      let items = this.calendar_store.hotel_comparative_dates;
      if (Object.keys(items).length > 0 && items.hasOwnProperty(this.date)) {
        date_vs = items[this.date].date_vs;
      }
      return date_vs;
    },
    has_pvp_change_today() {
      return (
        this.pvpMlosChanges("pvp", "pickup0") &&
        this.pvpMlosChanges("pvp", "pickup0") !== 0
      );
    },
    comments() {
      return this.calendar_store.hotel_comments.filter(
        c => c.date === this.date
      );
    },
    // Avisos de pricing del día. Cuelgan de la recomendación, pero no proponen
    // precio: se enseñan como los comentarios y los eventos.
    alerts() {
      const suggestion = this.daily_suggestions[this.date];
      return ((suggestion && suggestion.suggestions) || []).filter(
        s => s.suggestion_type === "notify"
      );
    },
    ...mapState({
      rms: state => state.rms,
      calendar_store: state => state.calendar,
      roomtype_types_mapped: state => state.roomtype.types_mapped,
      daily_suggestions: state => state.rms.daily_suggestions,
      all_kpis_daily_compset: state => state.rms.all_kpis_daily_compset
    }),
    ...mapGetters({
      current_hotel: "current_hotel",
      get_kpi: "get_kpi"
    })
  },
  mounted() {
    this.initKpisData();
  },
  watch: {
    configStrategyTable: {
      handler(newVal) {
        // Get unique kpis from columns
        this.initKpisData();
      }
    }
  }
};
</script>

<style scoped lang="scss">
tr {
  td {
    border: none;
    background-color: white;
    border-left: $borderType $borderColorV;
    border-bottom: $borderType $borderColorV;
    &:nth-child(1) {
      box-shadow: inset -2px 0 0 0 #ccc;
      position: sticky;
      left: 0;
      background: white;
      color: #e0e0e0;
      font-weight: normal;
      z-index: 2;
      padding: 3px 5px;
      .cell {
        min-height: 35px;
      }
    }
    &.sticky-columns-shadow {
      box-shadow: 1px 0px 0px 0px rgba(0, 0, 0, 0.15),
        2px 0px 0px 0px rgba(0, 0, 0, 0.12), 4px 0px 0px 0px rgba(0, 0, 0, 0.08);
    }
    &.group-otb {
      position: sticky;
      z-index: 1;
    }
    &.group-otb:nth-child(2) {
      left: calc(var(--widthColumnDate) + 1px);
    }
    &.group-otb:nth-child(3) {
      left: calc(var(--widthColumnDate) + var(--widthColumnColumns) + 1px);
    }
    &.group-otb:nth-child(4) {
      left: calc(var(--widthColumnDate) + var(--widthColumnColumns) * 2 + 1px);
    }
    &:hover {
      background-color: #f5fdff !important;
    }
    &.rate-change-bg {
      background-color: #f1f1f1 !important;
    }
    &.kpi-percent-range-0-20 {
      background-color: rgb(240, 218, 205) !important;
      &.summary {
        background-color: #f0d9cc !important;
      }
      .percent-progress {
        background-color: rgba(181, 68, 3, 1) !important;
      }
    }
    &.kpi-percent-range-20-40 {
      background-color: rgb(243, 230, 204) !important;
      &.summary {
        background-color: #f7e6cc !important;
      }
      .percent-progress {
        background-color: rgba(219, 131, 0, 1) !important;
      }
    }
    &.kpi-percent-range-40-60 {
      background-color: rgb(249, 239, 204) !important;
      &.summary {
        background-color: #fdf1cc !important;
      }
      .percent-progress {
        background-color: rgba(246, 185, 0, 1) !important;
      }
    }
    &.kpi-percent-range-60-80 {
      background-color: rgb(243, 239, 205) !important;
      &.summary {
        background-color: #ebe8cc !important;
      }
      .percent-progress {
        background-color: rgba(158, 143, 2, 1) !important;
      }
    }
    &.kpi-percent-range-80-100 {
      background-color: rgb(223, 239, 205) !important;
      &.summary {
        background-color: #dbe6cc !important;
      }
      .percent-progress {
        background-color: rgba(79, 131, 2, 1) !important;
      }
    }
    &.day-data {
      font-size: 0.8rem;
      font-weight: 700;
      color: #555;
      text-transform: uppercase;
      cursor: pointer;
      &::after {
        content: " ";
        position: absolute;
        top: 0;
        left: 3px;
        height: calc(100% - 8px);
        width: 4px;
        background-color: grey;
        z-index: 10;
        border-radius: 5px;
        margin: 4px 0;
      }
      &.positive::after {
        background-color: rgb(23, 171, 112);
      }
      &.negative::after {
        background-color: rgb(172, 0, 0);
      }
      &.neutral::after {
        background-color: orange;
      }
    }
    &.weekend {
      background-color: #fffef4;
    }
    &.previous {
      opacity: 1;
      background-color: #e5e5e5;
      &:hover {
        opacity: 1;
      }
    }
    &.limit {
      background-color: rgb(240, 216, 209) !important;
    }
    &.isSelectedChangePrice {
      background-color: rgba(21, 118, 199, 0.12) !important;
    }
    &.td-suggestion-ignored {
      height: 1px;
    }
    .percent-progress {
      content: "";
      position: absolute;
      bottom: -5px;
      height: 2px;
      border-radius: 5px;
    }
    .cell {
      line-height: 16px;
      font-size: 12px;
      text-align: center;
      white-space: nowrap;
      min-width: 100%;
      text-wrap: wrap;
      text-transform: uppercase;
      justify-content: center;
      align-items: center;
      &.min-height {
        min-height: 40px;
        display: flex;
        .a-rms-apply-prices {
          width: 100%;
        }
      }
      .bottom-line {
        .col:first-child {
          flex: 0 0 55%;
          max-width: 55%;
        }
        .col:last-child {
          flex: 0 0 45%;
          max-width: 45%;
        }
      }
      .suggestion-ignored-overlay {
        z-index: 0 !important;
      }
      ::v-deep .kpi-wrapper-2 {
        .v-tooltip {
          display: block;
        }
      }
      ::v-deep .input-suggestion {
        input {
          color: #1576c7 !important;
        }
        .symbol {
          color: #1576c7 !important;
        }
      }
    }
  }

  &:hover {
    td:nth-child(1) {
      /* border-right-color: #2d6ece !important; */
      box-shadow: inset -2px 0 0 0 #2d6ece;
    }
  }

  &.summary {
    box-shadow: 0px -1px 0px 0px rgba(0, 0, 0, 0.15),
      0px -2px 0px 0px rgba(0, 0, 0, 0.12), 0px -4px 0px 0px rgba(0, 0, 0, 0.08);
    position: sticky;
    bottom: 0;
    z-index: 2;
    td {
      background-color: #ebebeb;
      .cell {
        padding: 5px 0 2px;
      }
      .percent-progress {
        bottom: -1px;
      }
    }
  }
}

.br-bolder {
  border-right: 1px solid #ccc !important;
}
.kpi-vs {
  color: #6e6e6e;
}
</style>
